import { useState, type ComponentType } from 'react';
import { LAYER_TYPES, type Layer, type LayerType } from '@liveforge/shared';
import { useDismiss } from '../../hooks/use-dismiss';
import {
  CameraIcon,
  EyeIcon,
  ImageIcon,
  LockIcon,
  PeopleIcon,
  PlusIcon,
  ScreenShareIcon,
  ShapeIcon,
  SlidersIcon,
  TemplateIcon,
  TextIcon,
  TrashIcon,
} from './icons';

const TYPE_LABEL: Record<LayerType, string> = {
  video: 'Video',
  auto: 'Participants',
  screenshare: 'Screen share',
  image: 'Image',
  text: 'Text',
  shape: 'Shape',
  template: 'Template',
};

/**
 * Kept beside the labels rather than in the icon file: which glyph stands for
 * a layer type is a fact about the studio, not about the drawing, and the two
 * halves of the same answer drift apart if they live in different files.
 */
const TYPE_ICON: Record<LayerType, ComponentType> = {
  video: CameraIcon,
  auto: PeopleIcon,
  screenshare: ScreenShareIcon,
  image: ImageIcon,
  text: TextIcon,
  shape: ShapeIcon,
  template: TemplateIcon,
};

export interface LayerPanelProps {
  /** Already ordered top-first, as the panel displays them. */
  layers: Layer[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: (type: LayerType) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, patch: Partial<Layer>) => void;
  onReorder: (id: string, toIndex: number) => void;
  /** Opens the properties panel for a layer. */
  onEdit: (id: string) => void;
}

/**
 * The layer list. Order here is stacking order: the top row is drawn last and
 * therefore appears in front, which is the convention every design tool uses
 * and the opposite of the array the renderer walks.
 */
export function LayerPanel({
  layers,
  selectedId,
  onEdit,
  onSelect,
  onAdd,
  onRemove,
  onUpdate,
  onReorder,
}: LayerPanelProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useDismiss<HTMLDivElement>(addOpen, () => setAddOpen(false));

  function handleDrop(index: number) {
    if (dragId) {
      onReorder(dragId, index);
    }
    setDragId(null);
    setDropIndex(null);
  }

  return (
    <section className="layer-panel">
      <div className="dock__header">
        <h2>Layers</h2>
        <div className="layer-panel__add" ref={addRef}>
          <button type="button" className="dock__add" onClick={() => setAddOpen((open) => !open)}>
            <PlusIcon />
            Add
          </button>
          {addOpen ? (
            <ul className="layer-panel__menu">
              {LAYER_TYPES.map((type) => {
                const Icon = TYPE_ICON[type];
                return (
                  <li key={type}>
                    <button
                      type="button"
                      className="layer-panel__menu-item"
                      onClick={() => {
                        onAdd(type);
                        setAddOpen(false);
                      }}
                    >
                      <Icon />
                      {TYPE_LABEL[type]}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      </div>

      {layers.length === 0 ? (
        <p className="muted layer-panel__empty">
          Nothing is composited yet. Add a video layer and point it at a participant.
        </p>
      ) : (
        <ul className="layer-list">
          {layers.map((layer, index) => {
            const LayerIcon = TYPE_ICON[layer.type];
            return (
              <li
                key={layer.id}
                draggable
                onDragStart={() => setDragId(layer.id)}
                onDragEnd={() => {
                  setDragId(null);
                  setDropIndex(null);
                }}
                onDragOver={(event) => {
                  // Without this the drop never fires: the default handling of a
                  // dragover is to refuse the drop.
                  event.preventDefault();
                  setDropIndex(index);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  handleDrop(index);
                }}
                className={[
                  'layer-row',
                  layer.id === selectedId ? 'layer-row--selected' : '',
                  layer.id === dragId ? 'layer-row--dragging' : '',
                  dropIndex === index && dragId && dragId !== layer.id ? 'layer-row--drop' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <button
                  type="button"
                  className="layer-row__main"
                  onClick={() => onSelect(layer.id)}
                  title={layer.locked ? 'Locked - unlock to edit' : 'Select this layer'}
                >
                  {/*
                  The same glyph the Add menu offers, so what you picked and
                  what you got are recognisably the same thing. Decorative:
                  the type is spelled out at the other end of the row.
                */}
                  <LayerIcon aria-hidden="true" />
                  <span className="layer-row__name">{layer.name}</span>
                  <span className="layer-row__type muted">{TYPE_LABEL[layer.type]}</span>
                </button>

                <div className="layer-row__actions">
                  <button
                    type="button"
                    className="icon-button"
                    title="Properties"
                    onClick={() => {
                      onSelect(layer.id);
                      onEdit(layer.id);
                    }}
                  >
                    <SlidersIcon />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-pressed={layer.visible}
                    title={layer.visible ? 'Hide' : 'Show'}
                    onClick={() => onUpdate(layer.id, { visible: !layer.visible })}
                  >
                    <EyeIcon on={layer.visible} />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-pressed={layer.locked}
                    title={layer.locked ? 'Unlock' : 'Lock'}
                    onClick={() => onUpdate(layer.id, { locked: !layer.locked })}
                  >
                    <LockIcon locked={layer.locked} />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    title="Delete this layer"
                    onClick={() => onRemove(layer.id)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
